import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { Plus, Clock, Users2, Eye, SpellCheck, MessageSquare } from 'lucide-react';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { toast } from 'sonner';
import { cn } from '@shared/lib/utils';
import { Note, CollaboratorPermission } from '@shared/types';
import { api } from '@shared/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@shared/lib/queryKeys';
import { useAuth } from '@shared/contexts/AuthContext';
import { useNotesQuery, useCreateOrUpdateNoteMutation, useOptimisticNoteUpdate } from '@shared/hooks/useNotesQuery';
import { useSharedNotesQuery, useCollaboratorsQuery } from '@shared/hooks/useSharedNotesQuery';
import { useOfflineSync } from '@shared/hooks/useOfflineSync';
import { useLanguageTool } from '@shared/hooks/useLanguageTool';
import { offlineDataManager } from '@shared/lib/offlineData';
import { createCollabSession, caretColorFor, CollabSession } from '@shared/lib/collab';
import { RichTextEditor } from '@shared/components/note/RichTextEditor';
import RecentNotesPanel from '@shared/components/note/RecentNotesPanel';
import ShareNoteModal from '@shared/components/notes/ShareNoteModal';
import BaseNav from '@shared/components/common/BaseNav';
import { Button } from '@shared/components/ui/button';
import FloatingNav from '@/components/common/FloatingNav';
import CommentsPanel from '@/components/notes/CommentsPanel';
import GrammarPanel from '@/components/notes/GrammarPanel';
import SpeechControls from '@/components/notes/SpeechControls';

// Helper to detect if note is a draft (not yet persisted, even locally)
const isDraft = (note: any): note is { id: string } =>
  note && !note.note_id && note.id && typeof note.id === 'string' && note.id.startsWith('draft-');

function tiptapToPlainText(content: any): string {
  if (!content) return '';
  let doc: any;
  if (typeof content === 'string') {
    try { doc = JSON.parse(content); } catch { return content; }
  } else { doc = content; }
  const extract = (node: any): string => {
    if (node.type === 'text') return node.text ?? '';
    if (node.content) return node.content.map(extract).join('');
    return '';
  };
  return (doc.content ?? []).map((n: any) => extract(n)).join('\n').trim();
}

type RightPanel = 'comments' | 'grammar' | null;

// Extension-style notes workspace: top nav, full-width editor, right slide-in
// notes panel. Routing (/notes/:note_id) is the only web-specific addition.
const NotesWorkspace = () => {
  const { note_id } = useParams<{ note_id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: notes = [] } = useNotesQuery();
  const { data: sharedData } = useSharedNotesQuery();
  const createOrUpdateNoteMutation = useCreateOrUpdateNoteMutation();
  const { removeNoteOptimistically } = useOptimisticNoteUpdate();

  const [selectedNote, setSelectedNote] = useState<Note | any | null>(null);
  // Non-null while viewing a note someone shared with us (drives read-only vs
  // edit and routes saves through the online API instead of the offline queue).
  const [sharedPermission, setSharedPermission] = useState<CollaboratorPermission | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Live co-editing (Yjs) session for the open note, when it's shared.
  const [session, setSession] = useState<CollabSession | null>(null);
  const [liveStatus, setLiveStatus] = useState<'off' | 'connecting' | 'connected' | 'error'>('off');
  const [liveUsers, setLiveUsers] = useState(1);
  const [showRecentNotes, setShowRecentNotes] = useState(false);
  const [rightPanel, setRightPanel] = useState<RightPanel>(null);
  const editorRef = useRef<any>(null);

  const { check: ltCheck, matches: ltMatches, isChecking: ltChecking, error: ltError, clear: ltClear } =
    useLanguageTool();

  // ── URL → selection ────────────────────────────────────────────────────────
  useEffect(() => {
    if (note_id === 'new') {
      const folderId = searchParams.get('folder_id');
      setSharedPermission(null);
      setSelectedNote({
        id: `draft-${Date.now()}`,
        title: '',
        content: '',
        folder_id: folderId || null,
        sequence: 1,
        status: 'ACTIVE',
      });
      return;
    }
    if (!note_id) {
      // "/" — default to the most recently updated note
      if (notes.length > 0 && !selectedNote) {
        const mostRecent = [...notes].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
        setSelectedNote(mostRecent);
      }
      return;
    }
    if (selectedNote?.note_id === note_id) return;
    const own = notes.find(n => n.note_id === note_id);
    if (own) {
      setSharedPermission(null);
      setSelectedNote(own);
      return;
    }
    // Deep link to a note shared with me — restore its permission too.
    const shared = sharedData?.notes?.find(n => n.note_id === note_id);
    if (shared) {
      setSharedPermission(sharedData?.permissions?.[note_id] ?? 'VIEW');
      setSelectedNote(shared);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note_id, notes, sharedData]);

  // After a manual sync, refresh the open note if the pull brought a newer
  // version (e.g. a collaborator edited it) — the editor otherwise keeps
  // showing its pre-sync snapshot.
  const { lastSyncTime } = useOfflineSync();
  useEffect(() => {
    // Live notes are kept fresh by Yjs — never overwrite the editor from sync.
    if (!selectedNote?.note_id || sharedPermission || session || isDraft(selectedNote)) return;
    const fresh = offlineDataManager.getNotes().find(n => n.note_id === selectedNote.note_id);
    if (fresh && fresh.updated_at !== selectedNote.updated_at) {
      setSelectedNote(fresh);
      if (editorRef.current) {
        try {
          const json = typeof fresh.content === 'string' ? JSON.parse(fresh.content) : fresh.content;
          editorRef.current.commands.setContent(json ?? { type: 'doc', content: [] }, false);
        } catch {
          // Non-JSON content — let the editor keep its current state.
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSyncTime]);

  // ── Selection handlers ─────────────────────────────────────────────────────
  const handleNewNote = () => {
    setSharedPermission(null);
    navigate('/notes/new');
  };

  const handleSelectOwnNote = (note: Note) => {
    setSharedPermission(null);
    setSelectedNote(note);
    navigate(`/notes/${note.note_id}`);
  };

  const handleSelectSharedNote = (note: Note, permission: CollaboratorPermission) => {
    setSharedPermission(permission);
    setSelectedNote(note);
    navigate(`/notes/${note.note_id}`);
  };

  // Move one of my notes into a folder (offline-first: local + sync queue)
  const handleMoveNote = (note: Note, folderId: string) => {
    createOrUpdateNoteMutation.mutate({ ...note, folder_id: folderId });
    if (selectedNote?.note_id === note.note_id) {
      setSelectedNote({ ...note, folder_id: folderId });
    }
    toast.success('Note moved');
  };

  const handleDeleteNote = (nid: string) => {
    const note = notes.find(n => n.note_id === nid);
    removeNoteOptimistically(nid);
    if (note) {
      createOrUpdateNoteMutation.mutate({ ...note, status: 'INACTIVE' });
    }
    if (selectedNote?.note_id === nid) {
      setSelectedNote(null);
      navigate('/', { replace: true });
    }
  };

  const canEditSelected = !sharedPermission || sharedPermission === 'EDIT';
  const canShareSelected = !!selectedNote && !sharedPermission && !isDraft(selectedNote)
    && !!selectedNote.note_id && !String(selectedNote.note_id).startsWith('local-');

  // ── Live co-editing ────────────────────────────────────────────────────────
  // Eligible when: viewing a note shared with me (any permission — viewers see
  // updates live), or my own synced note that has collaborators.
  const ownSyncedNoteId = canShareSelected ? selectedNote!.note_id : null;
  const { data: myNoteCollaborators = [] } = useCollaboratorsQuery(ownSyncedNoteId);
  const liveNoteId = selectedNote?.note_id && !isDraft(selectedNote)
    && (sharedPermission || myNoteCollaborators.length > 0)
      ? selectedNote.note_id
      : null;

  useEffect(() => {
    if (!liveNoteId) {
      setLiveStatus('off');
      return;
    }
    const s = createCollabSession(liveNoteId);
    setSession(s);
    setLiveStatus('connecting');

    s.provider.on('status', ({ status }: { status: string }) => {
      setLiveStatus(prev => (prev === 'error' ? prev : status === 'connected' ? 'connected' : 'connecting'));
    });
    s.provider.on('authenticationFailed', () => {
      // No access / bad token — fall back to the non-live editor.
      setLiveStatus('error');
      setSession(null);
      s.destroy();
    });
    const awareness = s.provider.awareness;
    const onAwareness = () => setLiveUsers(awareness?.getStates().size ?? 1);
    awareness?.on('change', onAwareness);

    return () => {
      awareness?.off('change', onAwareness);
      s.destroy();
      setSession(null);
      setLiveUsers(1);
      setLiveStatus('off');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveNoteId]);

  const isLive = !!session;

  // TipTap major differs between apps, so the shared editor takes the
  // pre-configured collab extensions from the consuming app (v3 here).
  const collabConfig = useMemo(() => session ? {
    key: session.provider,
    extensions: [
      Collaboration.configure({ document: session.ydoc }),
      CollaborationCaret.configure({
        provider: session.provider,
        user: {
          name: user?.name || 'Someone',
          color: caretColorFor(user?.user_id || 'me'),
        },
      }),
    ],
  } : undefined, [session, user?.name, user?.user_id]);

  // ── Saving ─────────────────────────────────────────────────────────────────
  function handleUpdateNote(updatedNote: Note) {
    setSelectedNote(updatedNote);

    if (sharedPermission || isLive) {
      // Shared or live note: online-only save, straight to the API (never the
      // offline queue). In live mode the collab server persists content on its
      // own; this direct save mainly carries title changes and acts as the
      // fallback when the live socket is down.
      if (sharedPermission && sharedPermission !== 'EDIT') return; // read-only permissions
      api.createOrUpdateNote({
        note_id: updatedNote.note_id,
        title: updatedNote.title,
        content: updatedNote.content,
        status: updatedNote.status || 'ACTIVE',
        sequence: updatedNote.sequence || 1,
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.notes.shared() });
        })
        .catch((error: any) => {
          const status = error?.response?.status;
          toast.error(status === 403
            ? "You don't have edit access to this note anymore."
            : 'Failed to save shared note — check your connection.');
        });
      return;
    }

    // Own note: offline-first mutation (local store + sync queue)
    createOrUpdateNoteMutation.mutate(updatedNote);
  }

  // On first edit, persist the draft (offline-first) and move to its real URL
  async function handleFirstEdit(field: 'title' | 'content', value: string) {
    if (!selectedNote) return;
    if (isDraft(selectedNote)) {
      const status = selectedNote.status && (selectedNote.status === 'ACTIVE' || selectedNote.status === 'INACTIVE')
        ? selectedNote.status
        : 'ACTIVE';
      const created = await createOrUpdateNoteMutation.mutateAsync({
        title: field === 'title' ? value : selectedNote.title || 'Untitled Note',
        content: field === 'content' ? value : selectedNote.content || '',
        folder_id: selectedNote.folder_id || null,
        sequence: selectedNote.sequence ?? 1,
        status,
      });
      setSelectedNote(created);
      navigate(`/notes/${created.note_id}`, { replace: true });
    } else {
      handleUpdateNote({ ...selectedNote, [field]: value });
    }
  }

  // Autosave own-note content while typing (cheap local mutation). Shared/live
  // notes save on blur only, so remote echoes never trigger API writes.
  const debouncedOwnSave = useMemo(
    () => debounce((note: Note) => createOrUpdateNoteMutation.mutate(note), 600),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => () => debouncedOwnSave.flush(), [debouncedOwnSave]);

  const setSelectedNoteTitle = (title: string) => {
    setSelectedNote((prev: any) => prev ? { ...prev, title } : prev);
  };

  const handleEditorReady = (editor: any) => {
    editorRef.current = editor;
  };

  // ── Grammar (LanguageTool panel) ───────────────────────────────────────────
  const handleOpenGrammar = async () => {
    const isOpen = rightPanel === 'grammar';
    setRightPanel(isOpen ? null : 'grammar');
    if (!isOpen && editorRef.current) {
      const text = editorRef.current.getText?.() ?? tiptapToPlainText(selectedNote?.content);
      await ltCheck(text);
    } else if (isOpen) {
      ltClear();
    }
  };

  const handleApplySuggestion = useCallback((match: any, replacement: string) => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    let found = { from: -1, to: -1 };
    editor.state.doc.descendants((node: any, pos: number) => {
      if (found.from >= 0) return false;
      if (node.isText) {
        const idx = node.text.indexOf(match.context.text.slice(match.context.offset, match.context.offset + match.length));
        if (idx >= 0) {
          found = { from: pos + idx, to: pos + idx + match.length };
          return false;
        }
      }
    });
    if (found.from >= 0) {
      editor.chain().focus().deleteRange(found).insertContentAt(found.from, replacement).run();
      toast.success(`Replaced with "${replacement}"`);
    } else {
      toast.info('Apply manually — could not locate text.');
    }
  }, []);

  const getPlainText = useCallback(() => {
    if (editorRef.current?.getText) return editorRef.current.getText();
    return tiptapToPlainText(selectedNote?.content);
  }, [selectedNote]);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      <BaseNav
        leftActions={
          <>
            <Button
              variant={showRecentNotes ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setShowRecentNotes(!showRecentNotes)}
              className="flex items-center gap-2"
            >
              <Clock className="h-4 w-4" />
              <span>Recent Notes</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewNote}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span>New Note</span>
            </Button>
          </>
        }
      />

      <div className="mt-14 flex h-[calc(100vh-3.5rem)]">
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 md:px-6 py-4">
            <div className={showRecentNotes ? "max-w-5xl pl-0" : "max-w-full"}>
              {/* Title and actions row */}
              <div className="flex gap-8 mb-4">
                <input
                  type="text"
                  value={selectedNote?.title || ''}
                  onChange={e => {
                    if (selectedNote) {
                      setSelectedNoteTitle(e.target.value);
                    }
                  }}
                  onBlur={async (e) => {
                    if (selectedNote) {
                      if (isDraft(selectedNote)) {
                        handleFirstEdit('title', e.target.value);
                      } else {
                        handleUpdateNote({ ...selectedNote, title: e.target.value });
                      }
                    }
                  }}
                  placeholder="Note title"
                  readOnly={!canEditSelected}
                  className="text-2xl font-semibold bg-transparent border-none focus:outline-none w-full text-left"
                />
                <div className="flex items-center gap-2">
                  {/* Live co-editing status */}
                  {(session || liveStatus === 'error') && (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent px-2.5 py-1 text-xs text-muted-foreground">
                      <span className={cn(
                        'h-2 w-2 rounded-full',
                        liveStatus === 'connected' ? 'bg-green-500 animate-pulse'
                          : liveStatus === 'error' ? 'bg-red-500'
                          : 'bg-amber-500'
                      )} />
                      {liveStatus === 'connected'
                        ? `Live · ${liveUsers} online`
                        : liveStatus === 'error'
                          ? 'Live sync unavailable'
                          : 'Connecting…'}
                    </span>
                  )}
                  {/* Shared-note badge */}
                  {sharedPermission && (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent px-2.5 py-1 text-xs text-muted-foreground">
                      {sharedPermission === 'EDIT' ? <Users2 className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {sharedPermission === 'EDIT' ? 'Shared · can edit' : 'Shared · view only'}
                    </span>
                  )}
                  {/* Share (own, synced notes only) */}
                  {!sharedPermission && (
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-accent/50 transition disabled:opacity-40"
                      title={canShareSelected ? 'Share note' : 'Sync this note first to share it'}
                      onClick={() => canShareSelected && setShareOpen(true)}
                      disabled={!canShareSelected}
                    >
                      <Users2 className="w-5 h-5" />
                    </button>
                  )}
                  {/* Comments */}
                  {selectedNote?.note_id && (
                    <button
                      type="button"
                      className={cn(
                        'p-2 rounded-lg hover:bg-accent/50 transition',
                        rightPanel === 'comments' && 'bg-accent text-primary'
                      )}
                      title="Comments"
                      onClick={() => setRightPanel(prev => prev === 'comments' ? null : 'comments')}
                    >
                      <MessageSquare className="w-5 h-5" />
                    </button>
                  )}
                  {/* Grammar check (LanguageTool) */}
                  <button
                    type="button"
                    className={cn(
                      'relative p-2 rounded-lg hover:bg-accent/50 transition',
                      rightPanel === 'grammar' && 'bg-accent text-primary'
                    )}
                    title="Check Grammar"
                    onClick={handleOpenGrammar}
                  >
                    <SpellCheck className="w-5 h-5" />
                    {ltMatches.length > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-tight">
                        {ltMatches.length}
                      </span>
                    )}
                  </button>
                  {/* Read aloud */}
                  <SpeechControls getText={getPlainText} />
                </div>
              </div>

              {/* Content */}
              {selectedNote && (
                <RichTextEditor
                  key={`${selectedNote.note_id ?? 'draft'}:${session ? 'live' : 'solo'}`}
                  collab={collabConfig}
                  value={
                    !selectedNote.content
                      ? { type: 'doc', content: [] }
                      : typeof selectedNote.content === 'string'
                        ? (() => { try { return JSON.parse(selectedNote.content); } catch { return { type: 'doc', content: [] }; } })()
                        : selectedNote.content
                  }
                  noteId={selectedNote.note_id}
                  onChange={newContent => {
                    if (selectedNote) {
                      const contentString = typeof newContent === 'string' ? newContent : JSON.stringify(newContent);
                      const updated = { ...selectedNote, content: contentString };
                      setSelectedNote(updated);
                      if (!sharedPermission && !isLive && !isDraft(selectedNote)) {
                        debouncedOwnSave(updated);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (selectedNote) {
                      const contentString = typeof selectedNote.content === 'string' ? selectedNote.content : JSON.stringify(selectedNote.content);
                      if (isDraft(selectedNote)) {
                        handleFirstEdit('content', contentString);
                      } else {
                        handleUpdateNote({ ...selectedNote, content: contentString });
                      }
                    }
                  }}
                  placeholder="Start writing..."
                  editorReady={handleEditorReady}
                  editable={canEditSelected}
                />
              )}
            </div>
          </div>
        </main>

        {/* Right panels */}
        {rightPanel === 'grammar' && (
          <GrammarPanel
            matches={ltMatches}
            isChecking={ltChecking}
            error={ltError}
            onApplySuggestion={handleApplySuggestion}
            onClose={() => { setRightPanel(null); ltClear(); }}
          />
        )}
        {rightPanel === 'comments' && selectedNote?.note_id && (
          <CommentsPanel
            noteId={selectedNote.note_id}
            onClose={() => setRightPanel(null)}
          />
        )}
      </div>

      {showRecentNotes && (
        <RecentNotesPanel
          notes={notes}
          selectedNoteId={selectedNote?.note_id}
          onSelectNote={handleSelectOwnNote}
          onSelectSharedNote={handleSelectSharedNote}
          onMoveNote={handleMoveNote}
          onClose={() => setShowRecentNotes(false)}
          onDeleteNote={handleDeleteNote}
        />
      )}

      {/* Share modal (own notes only) */}
      {selectedNote?.note_id && !sharedPermission && (
        <ShareNoteModal
          noteId={selectedNote.note_id}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          isOwner
        />
      )}

      <FloatingNav
        onToggleNotes={() => setShowRecentNotes(!showRecentNotes)}
        showNotes={showRecentNotes}
      />
    </div>
  );
};

export default NotesWorkspace;
